import { useState, useRef, useCallback } from 'react';

/**
 * Detect the best supported audio MIME type for MediaRecorder.
 * Tries webm first (Chrome/Firefox), then mp4 (Safari), then default.
 */
function getSupportedMimeType() {
  if (typeof MediaRecorder === 'undefined') return null;
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    ''  // empty = browser default
  ];
  for (const type of types) {
    if (!type) return { mimeType: '', ext: 'webm' };
    try {
      if (MediaRecorder.isTypeSupported(type)) {
        const ext = type.includes('mp4') ? 'mp4' : type.includes('ogg') ? 'ogg' : 'webm';
        return { mimeType: type, ext };
      }
    } catch {}
  }
  return { mimeType: '', ext: 'webm' };
}

/**
 * useAudioRecorder — cross-browser audio recording hook.
 * Works on Chrome, Firefox, Safari, iOS Safari, Android Chrome.
 */
export function useAudioRecorder() {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const streamRef = useRef(null);

  const startRecording = useCallback(async () => {
    setError(null);
    setAudioBlob(null);
    setAudioUrl(null);

    // Check support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Your browser does not support audio recording. Please use Chrome, Firefox, or Safari.');
      return false;
    }

    if (typeof MediaRecorder === 'undefined') {
      setError('MediaRecorder not available. Please update your browser.');
      return false;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const format = getSupportedMimeType();
      const options = format.mimeType ? { mimeType: format.mimeType } : {};

      let mr;
      try {
        mr = new MediaRecorder(stream, options);
      } catch {
        // Fallback: no options
        mr = new MediaRecorder(stream);
      }

      chunks.current = [];

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunks.current.push(e.data);
        }
      };

      mr.onstop = () => {
        const mimeType = mr.mimeType || format.mimeType || 'audio/webm';
        const blob = new Blob(chunks.current, { type: mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
      };

      mr.onerror = (e) => {
        setError('Recording error occurred. Please try again.');
        setRecording(false);
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.current = mr;
      mr.start(100); // Collect data every 100ms for smoother recording
      setRecording(true);
      return true;
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Microphone access was denied. Please allow microphone access in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else {
        setError(`Could not start recording: ${err.message}`);
      }
      return false;
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
    setRecording(false);
  }, []);

  const reset = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setError(null);
    chunks.current = [];
  }, [audioUrl]);

  return {
    recording,
    audioBlob,
    audioUrl,
    error,
    startRecording,
    stopRecording,
    reset,
    isSupported: typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices
  };
}
